"use client";

import { useCallback, useState } from "react";
import { toast } from "sonner";

import {
  selectWishlistCount,
  selectWishlistItems,
  selectWishlistVariantIdsSet,
  wishlistApi,
  useGetWishlistQuery,
  useLazyGetWishlistQuery,
  useToggleWishlistMutation,
} from "../wishlistApi";
import type { NormalizedWishlist } from "../types";
import { useAppSelector } from "@/src/store/hooks";

function getWishlistErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";
  return message || "تعذر تحميل المفضلة الآن";
}

function getWishlistToggleMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";
  return message || "تعذر تحديث المفضلة الآن";
}

export function useWishlist(options?: { autoLoad?: boolean }) {
  const autoLoad = options?.autoLoad ?? false;
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isAuthInitialized = useAppSelector((state) => state.auth.isInitialized);
  const wishlistItems = useAppSelector(selectWishlistItems);
  const wishlistVariantIdsSet = useAppSelector(selectWishlistVariantIdsSet);
  const count = useAppSelector(selectWishlistCount);
  const wishlistState = wishlistApi.endpoints.getWishlist.useQueryState(undefined);
  const {
    data: queriedWishlist,
    error: queriedWishlistError,
    isFetching: isWishlistFetching,
    isLoading: isWishlistLoading,
    refetch,
  } = useGetWishlistQuery(undefined, {
    skip: !autoLoad || !isAuthInitialized,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [triggerGetWishlist] = useLazyGetWishlistQuery();
  const [toggleWishlistMutation] = useToggleWishlistMutation();
  const wishlist = (queriedWishlist ?? wishlistState.data ?? null) as NormalizedWishlist | null;
  const variantIds = Array.from(wishlistVariantIdsSet);
  const [isMutating, setIsMutating] = useState(false);
  const [pendingVariantId, setPendingVariantId] = useState<number | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const reloadWishlist = useCallback(async () => {
    setMutationError(null);

    if (autoLoad) {
      await refetch();
      return;
    }

    await triggerGetWishlist(undefined);
  }, [autoLoad, refetch, triggerGetWishlist]);

  const toggleItem = useCallback(
    async (variantId: number) => {
      if (!isAuthInitialized) return null;

      setIsMutating(true);
      setPendingVariantId(variantId);
      setMutationError(null);

      try {
        const result = await toggleWishlistMutation({ variantId }).unwrap();
        toast.success(result.message);
        return result;
      } catch (error) {
        setMutationError(getWishlistToggleMessage(error));
        toast.error(getWishlistToggleMessage(error));
        return null;
      } finally {
        setIsMutating(false);
        setPendingVariantId(null);
      }
    },
    [isAuthInitialized, toggleWishlistMutation],
  );

  const isInWishlist = useCallback(
    (variantId: number | null) =>
      typeof variantId === "number" && wishlistVariantIdsSet.has(variantId),
    [wishlistVariantIdsSet],
  );
  const queryError = queriedWishlistError || wishlistState.error;
  const error = mutationError
    ? mutationError
    : queryError
      ? getWishlistErrorMessage(queryError)
      : null;
  const isLoading =
    !isAuthInitialized ||
    (autoLoad && (isWishlistLoading || isWishlistFetching) && !wishlist);

  return {
    wishlist,
    isLoading,
    isMutating,
    pendingVariantId,
    error,
    isAuthenticated,
    isAuthInitialized,
    wishlistItems,
    wishlistVariantIdsSet,
    variantIds,
    count,
    isInWishlist,
    reloadWishlist,
    toggleItem,
  };
}
